import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { BlogArticle, articleHead, type Article } from "@/components/marketing/BlogArticle";

const article: Article = {
  path: "/blog/what-is-an-ai-twin",
  category: "Guide",
  readTime: "9 min read",
  published: "2026-07-20",
  title: "What is an AI Twin?",
  titleEm: "The complete 2026 guide.",
  lead: "An AI Twin is a conversational, always-on digital version of your company — trained on your own knowledge, able to answer customers, qualify leads and even talk to other companies' twins. Here's exactly what that means, how it differs from a chatbot, and how to build one.",
  metaDescription:
    "What an AI Twin is, how it differs from a chatbot, and why grounding answers in your own material is what makes it usable in B2B sales.",
  takeaways: [
    "An AI Twin is a reasoning agent grounded in your company's own knowledge — not a scripted chatbot.",
    "It answers visitors, qualifies and scores leads, and hands warm ones to your team with context.",
    "Unlike an industrial 'digital twin', an AI Twin models a company's knowledge, offer and voice.",
    "You can launch one from your website in about 60 seconds and improve it over time.",
  ],
  blocks: [
    { type: "h2", text: "The short definition" },
    {
      type: "p",
      text: (
        <>
          An <strong className="text-foreground">AI Twin</strong> is a persistent, conversational AI
          agent that represents a specific company. It is trained on that company's own truth —
          website, documents, product catalog, pricing and policies — and can hold real
          conversations with customers, partners and investors, 24/7, in the company's voice, with
          citations back to the source.
        </>
      ),
    },
    {
      type: "p",
      text: "In practice, an AI Twin does three jobs at once: it answers questions accurately, it qualifies and captures leads, and it routes serious opportunities to a human with the context already written up. Think of it as the always-on front door to your business.",
    },
    { type: "h2", text: "AI Twin vs. chatbot: what actually changes" },
    {
      type: "p",
      text: "A traditional chatbot follows a decision tree or answers from a single generic prompt. It breaks the moment a visitor asks something specific. An AI Twin is grounded in your knowledge graph and reasons across it — so every answer is cited, memory persists per visitor, and the same twin can meet other twins in a network to broker introductions.",
    },
    {
      type: "table",
      head: ["", "Chatbot", "AI Twin"],
      rows: [
        ["Knowledge", "Fixed FAQ / one prompt", "Your whole site, docs, pricing — cited"],
        ["Reasoning", "Intent tree", "Adaptive, per-conversation"],
        ["Leads", "Drops into a form", "Qualifies, scores, routes with a brief"],
        ["Memory", "None or per-session", "Persistent per visitor"],
        ["Network", "Isolated", "Can meet other companies' twins"],
      ],
    },
    { type: "h2", text: "AI Twin vs. digital twin" },
    {
      type: "p",
      text: "The term 'digital twin' comes from industry: a live software model of a physical asset like a turbine or a factory line, used for simulation. An AI Twin borrows the idea but points it at a company's knowledge, offer and voice instead of a machine — so the thing it simulates is a real conversation with your business.",
    },
    { type: "h2", text: "What an AI Twin does, concretely" },
    {
      type: "ul",
      items: [
        "Answers product, pricing and policy questions instantly, grounded in your docs.",
        "Qualifies inbound visitors against your ideal-customer profile and scores intent.",
        "Captures leads with the full conversation context and pushes them to your CRM.",
        "Books meetings and hands warm leads to a human with a written brief.",
        "Publishes a public profile so other companies — and their twins — can discover you.",
      ],
    },
    { type: "h2", text: "How an AI Twin is built" },
    {
      type: "ol",
      items: [
        <>
          <strong className="text-foreground">Ingest.</strong> Point the twin at your website; it
          crawls and builds a citable knowledge graph, then you add documents.
        </>,
        <>
          <strong className="text-foreground">Tune.</strong> Set voice, tone, boundaries and how it
          should qualify and hand off leads.
        </>,
        <>
          <strong className="text-foreground">Embed.</strong> Drop one script tag on your site, or
          use the full-page and API surfaces.
        </>,
        <>
          <strong className="text-foreground">Improve.</strong> Every conversation and unanswered
          question feeds back so the twin gets sharper each week.
        </>,
      ],
    },
    {
      type: "callout",
      title: "Grounding is everything",
      text: "The difference between a demo and a system is grounding. If answers aren't tied to your real knowledge base, the model will invent them. An AI Twin treats hallucinations as bugs — if it can't cite a source, it says so and offers a human.",
    },
    { type: "h2", text: "Who an AI Twin is for" },
    {
      type: "p",
      text: "Founders answering the same questions at midnight, support teams drowning in repetitive tickets, and sales teams qualifying leads that never close. Anyone whose website gets traffic but leaves most of it un-served is a candidate — the twin handles the first conversation so the team focuses on the ones that matter.",
    },
  ],
  faqs: [
    {
      q: "What is an AI Twin in simple terms?",
      a: "An AI Twin is a conversational digital version of your company, trained on your own website and documents, that can answer customers, qualify leads and route serious ones to your team 24/7 — with citations back to the source.",
    },
    {
      q: "How is an AI Twin different from ChatGPT?",
      a: "ChatGPT is a general model. An AI Twin is grounded specifically in one company's knowledge and acts on its behalf — answering with cited facts from that company, scoring leads against its ICP and writing them to its CRM.",
    },
    {
      q: "How long does it take to build an AI Twin?",
      a: "About 60 seconds for a first version from your website. Uploading extra documents, tuning personality and connecting your CRM takes a bit longer but is still measured in days, not months.",
    },
    {
      q: "Do I need to code to launch an AI Twin?",
      a: "No. Setup and the embeddable widget are no-code. Developers can additionally drive everything through a REST API and MCP server.",
    },
  ],
  related: [
    { slug: "ai-twin-vs-chatbot", title: "AI Twin vs. Chatbot: why static bots lose in 2026" },
    { slug: "ai-lead-qualification", title: "AI lead qualification for B2B: a 2026 playbook" },
    {
      slug: "best-ai-chatbot-alternatives-b2b",
      title: "The 7 best AI chatbot alternatives for B2B",
    },
  ],
};

export const Route = createFileRoute("/blog/what-is-an-ai-twin")({
  head: () =>
    articleHead(
      article,
      buildSeo({
        path: article.path,
        title: "What Is an AI Twin? The Complete 2026 Guide | Reality Twin",
        description: article.metaDescription ?? article.lead,
        type: "article",
      }),
    ),
  component: () => <BlogArticle article={article} />,
});
